import {
    type ApiResult,
    type BaseParams,
    type EmojiId,
    type OwnerResponse,
    type PaginatedResult,
    type PostParams,
    type ResourceId,
    type YatPageCollectionUpdate,
    type YatPageItemCreate,
    type YatPageItemNFTResponse,
    type YatPageItemResponse,
    type YatPageItemsResponse,
    type YatPageItemUpdate,
} from '@app/client';
import { apiReturnPaginatedResults, apiReturnResults } from '@app/client/apiFactory/apiReturn';
import { type CollectionDetailsDataType } from '@app/pages/NFTDetailView/components/Details/Details.data';
import {
    type NFTCollectionAssetDataType,
    type NFTCollectionDataType,
} from '@app/pages/NFTDetailView/components/MainStage/components/NFTInfo/NFTInfo.data';
import {
    type CollectionTraitsResponseDataType,
    type OwnerStatsType,
} from '@app/pages/NFTDetailView/components/TraitsPanel/NFTTraits.data';
import { type QueryItemsType } from '@app/store/actions/nft-manager';
import { type Currency } from '@app/store/types/nft-manager';

import { apiFactory } from '../../apiFactory/api';
import { Request } from '../../common';
import { links } from '../../links';

export async function addItem(
    params: BaseParams & PostParams<YatPageItemCreate>,
): Promise<ApiResult<YatPageItemResponse>> {
    return Request.postRequest<YatPageItemResponse>({
        ...params,
        target: links.yatPage().protected().item(),
        settings: {
            ...params.settings,
        },
    });
}

export async function getYatPageItems(
    params: BaseParams & { eid: EmojiId; pageId: ResourceId; isPageOwner: boolean },
): Promise<ApiResult<YatPageItemsResponse>> {
    const target = params.isPageOwner
        ? links.yatPage().protected().pageItems(params.pageId)
        : links.yatPage().public(params.eid).pageItems();

    return Request.getRequest<YatPageItemsResponse>({
        ...params,
        target: target,
        settings: {
            ...params.settings,
        },
    });
}

export async function getNFTCollectionAssets(
    params: BaseParams & { contract: string; itemId: string },
): Promise<ApiResult<NFTCollectionAssetDataType>> {
    const { contract, itemId } = params;
    const target = links.nft().public.collectionAssets(contract, itemId);

    return Request.getRequest<NFTCollectionAssetDataType>({
        ...params,
        target,
        settings: {
            ...params.settings,
        },
    });
}

export const callGetNFTCollectionAssets = (
    params: BaseParams & { contract: string; itemId: string },
): Promise<NFTCollectionAssetDataType> =>
    apiFactory()
        .call(getNFTCollectionAssets, {
            ...params,
            settings: {
                ...params.settings,
            },
        })
        .then((result) => {
            if (result.ok) {
                return result.value;
            }
            const errorMessage = result.error.detail ?? `Error getting asset ${params.contract}`;
            throw new Error(errorMessage);
        });

export const callGetLiquidIdItems = (
    params: BaseParams & { collectionId: string; emojiID: string },
): Promise<YatPageItemsResponse> =>
    apiFactory()
        .call(fetchLiquidIdCollectionItems, {
            ...params,
            collectionId: params.collectionId,
            eid: params.emojiID,
            isEditMode: false,
            pageId: null,
            value: {},
            settings: {
                ...params.settings,
            },
        })
        .then((result) => {
            if (result.ok) {
                return result.value;
            }
            const errorMessage = result.error.detail ?? `Error getting lid items ${params.emojiID}`;
            throw new Error(errorMessage);
        });

export async function getNFTCollectionDetails(params: BaseParams): Promise<ApiResult<NFTCollectionDataType>> {
    const target = links.nft().public.collectionTokens;
    return Request.getRequest<NFTCollectionDataType>({
        ...params,
        target,
        settings: {
            ...params.settings,
        },
    });
}

export const callGetNFTCollectionDetails = (params: BaseParams): Promise<NFTCollectionDataType> =>
    apiFactory()
        .call(getNFTCollectionDetails, {
            ...params,
            settings: {
                ...params.settings,
            },
        })
        .then((result) => {
            if (result.ok) {
                return result.value;
            }
            const errorMessage = result.error.detail ?? 'Error getting asset tokens';
            throw new Error(errorMessage);
        });

export async function getCollectionDetails(
    params: BaseParams & { contract: string },
): Promise<ApiResult<CollectionDetailsDataType>> {
    const { contract } = params;
    const formattedContract = contract.startsWith('0x') ? contract : `0x${contract}`;
    const target = links.nft().public.collectionDetails(formattedContract);

    return Request.getRequest<CollectionDetailsDataType>({
        ...params,
        target,
        settings: {
            ...params.settings,
        },
    });
}

export const callGetCollectionDetails = (
    params: BaseParams & { contract: string },
): Promise<CollectionDetailsDataType> =>
    apiFactory()
        .call(getCollectionDetails, {
            ...params,
            settings: {
                ...params.settings,
            },
        })
        .then((result) => {
            if (result.ok) {
                return result.value;
            }
            const errorMessage = result.error.detail ?? `Error getting collection ${params.contract}`;
            throw new Error(errorMessage);
        });

export async function getNFTOwners(
    params: BaseParams & { currency?: Currency; contract: string; itemId: string },
): Promise<ApiResult<PaginatedResult<OwnerResponse>>> {
    const { currency, contract, itemId } = params;
    const formattedContract = contract.startsWith('0x') ? contract : `0x${contract}`;
    const target = links.nft().public.owners(currency, formattedContract, itemId);

    return Request.getRequest<PaginatedResult<OwnerResponse>>({
        ...params,
        target,
        settings: {
            ...params.settings,
        },
    });
}
export const callGetNFTOwners = (params: BaseParams & { currency?: Currency; contract: string; itemId: string }) =>
    apiReturnPaginatedResults<OwnerResponse>(getNFTOwners, params);

export type UserAssetsFilterType = 'most_valuable' | 'recent_items';
export async function getUserAssets(
    params: BaseParams & { currency?: Currency; ownerId: string; filter: UserAssetsFilterType },
): Promise<ApiResult<YatPageItemsResponse>> {
    const { currency, ownerId, filter } = params;
    const target = links.nft().public.ownerItems(currency, ownerId, filter);

    return Request.getRequest<YatPageItemsResponse>({
        ...params,
        target,
        settings: {
            ...params.settings,
        },
    });
}

export const callGetUserAssets = (
    params: BaseParams & { currency?: Currency; ownerId: string; filter?: UserAssetsFilterType },
): Promise<YatPageItemsResponse> =>
    apiFactory()
        .call(getUserAssets, {
            ...params,
            filter: params.filter ?? 'recent_items',
            settings: {
                ...params.settings,
            },
        })
        .then((result) => {
            if (result.ok) {
                return result.value;
            }
            const errorMessage = result.error.detail ?? `Error getting owner assets ${params.ownerId}`;
            throw new Error(errorMessage);
        });

export async function getCollectionUserAssets(
    params: BaseParams & { currency?: Currency; contract: string; ownerId: string },
): Promise<ApiResult<YatPageItemsResponse>> {
    const { currency, ownerId, contract } = params;
    const formattedContract = contract.startsWith('0x') ? contract : `0x${contract}`;
    const target = links.nft().public.ownerCollectionItems(currency, formattedContract, ownerId);

    return Request.getRequest<YatPageItemsResponse>({
        ...params,
        target,
        settings: {
            ...params.settings,
        },
    });
}

export const callGetCollectionUserAssets = (
    params: BaseParams & { currency?: Currency; contract: string; ownerId: string },
): Promise<YatPageItemsResponse> =>
    apiFactory()
        .call(getCollectionUserAssets, {
            ...params,
            settings: {
                ...params.settings,
            },
        })
        .then((result) => {
            if (result.ok) {
                return result.value;
            }
            const errorMessage = result.error.detail ?? `Error getting owner assets ${params.ownerId}`;
            throw new Error(errorMessage);
        });

export async function getUserStats(params: BaseParams & { ownerId: string }): Promise<ApiResult<OwnerStatsType>> {
    const target = links.nft().public.stats(params.ownerId);

    return Request.getRequest<OwnerStatsType>({
        ...params,
        target,
        settings: {
            ...params.settings,
        },
    });
}

export const callGetUserStats = (params: BaseParams & { ownerId: string }) =>
    apiReturnResults<OwnerStatsType>(getUserStats, params);

export async function getCollectionTraits(
    params: BaseParams & { contract: string; itemId: string },
): Promise<ApiResult<CollectionTraitsResponseDataType>> {
    const { contract } = params;
    const formattedContract = contract.startsWith('0x') ? contract : `0x${contract}`;
    const target = links.nft().public.traits(formattedContract, params.itemId);

    return Request.getRequest<CollectionTraitsResponseDataType>({
        ...params,
        target,
        settings: {
            ...params.settings,
        },
    });
}

export const callGetCollectionTraits = (
    params: BaseParams & { contract: string; itemId: string },
): Promise<CollectionTraitsResponseDataType> =>
    apiFactory()
        .call(getCollectionTraits, {
            ...params,
            query: {
                maxLastSells: 2,
                limit: 1000,
            },
            settings: {
                ...params.settings,
            },
        })
        .then((result) => {
            if (result.ok) {
                return result.value;
            }
            const errorMessage =
                result.error.detail ?? `Error getting collection traits for contract ${params.contract}`;
            throw new Error(errorMessage);
        });

export async function getNFTDetails(
    params: BaseParams & { currency?: Currency; contract: string; itemId: string },
): Promise<ApiResult<YatPageItemNFTResponse>> {
    const { currency, contract, itemId } = params;
    const target = links.nft().public.details(currency, contract, itemId);

    return Request.getRequest<YatPageItemNFTResponse>({
        ...params,
        target,
        settings: {
            ...params.settings,
        },
    });
}

export const callGetNFTDetails = (
    params: BaseParams & { currency?: Currency; contract: string; itemId: string },
): Promise<YatPageItemNFTResponse> =>
    apiFactory()
        .call(getNFTDetails, {
            ...params,
            settings: {
                ...params.settings,
            },
        })
        .then((result) => {
            if (result.ok) {
                return result.value;
            }
            const errorMessage = result.error.detail ?? `Error getting asset ${params.contract}`;
            throw new Error(errorMessage);
        });

export async function updateYatPageItem(
    params: BaseParams & PostParams<YatPageItemUpdate>,
): Promise<ApiResult<YatPageItemsResponse>> {
    return Request.patchRequest<YatPageItemsResponse>({
        ...params,
        target: links.yatPage().protected().pageItem(),
        settings: {
            ...params.settings,
        },
    });
}

export async function updateYatPageCollectionItems(
    params: BaseParams & PostParams<YatPageCollectionUpdate> & { pageId: ResourceId },
): Promise<ApiResult<YatPageCollectionUpdate>> {
    return Request.postRequest<YatPageCollectionUpdate>({
        ...params,
        target: links.yatPage().protected().pageItems(params.pageId),
        settings: {
            ...params.settings,
        },
    });
}

export type SearchParamsItemAction = 'Add' | 'Remove';
export type PageItemsSearchParams = {
    query?: QueryItemsType;
    limit?: string;
    action?: SearchParamsItemAction;
    operator?: 'AND' | 'OR';
    dir?: 'Asc' | 'Desc';
    sort?: 'activity_date' | 'floor_price' | 'gains' | 'purchase_date' | 'timescore';
    page?: string;
    yat_page_id?: string | null;
};

export async function searchYatPageItems(
    params: BaseParams & PostParams<PageItemsSearchParams> & { token?: string; eid?: EmojiId },
): Promise<ApiResult<YatPageItemsResponse>> {
    const target = params.eid ? links.yatPage().public(params.eid).search() : links.yatPage().protected().search();
    return await Request.postRequest<YatPageItemsResponse>({
        ...params,
        target,
        settings: {
            ...params.settings,
        },
    });
}

async function updateAllQueryYatPageItems(
    params: BaseParams & PostParams<PageItemsSearchParams> & { pageId: string },
): Promise<ApiResult<YatPageItemsResponse>> {
    const target = links.yatPage().protected().pageItemsBulk(params.pageId);
    return await Request.postRequest<YatPageItemsResponse>({
        ...params,
        target,
        settings: {
            ...params.settings,
        },
    });
}

type FetchLiquidIdCollectionItemsParams = {
    eid: ResourceId;
    pageId: ResourceId | null;
    collectionId: ResourceId;
    isEditMode: boolean;
    standalone?: boolean;
};

export async function fetchLiquidIdCollectionItems(
    params: BaseParams & PostParams & FetchLiquidIdCollectionItemsParams,
): Promise<ApiResult<YatPageItemsResponse>> {
    const { eid, pageId, collectionId, isEditMode, standalone = false } = params;

    let target;

    if (isEditMode) {
        if (pageId === null) {
            return Promise.reject(new Error(`Invalid pageId: ${pageId}`));
        }
        target = links.yatPage().protected().pageCollectionItems(pageId);
    }

    // TODO - fix this - see if we can get the 'primary' pageId from /load too
    //  using the the public call for the standalone liquid id here instead of making a call to get the pageId (but probably not ideal to use the public endpoint?)
    if (standalone && pageId) {
        target = links.yatPage().protected().pageCollectionItems(pageId);
    }

    target = links.yatPage().public(eid).search();

    return Request.postRequest<YatPageItemsResponse>({
        ...params,
        target,
        value: {
            collection_id: collectionId,
        },
    });
}
/**
 * Update users NFTs based on query.
 */
export const updateAllQueryNFTs = (params: BaseParams & PageItemsSearchParams & { pageId: string }): Promise<unknown> =>
    apiFactory()
        .call(updateAllQueryYatPageItems, { value: params, pageId: params.pageId })
        .catch(() => {
            throw new Error('Error updating items');
        });
// TODO: Add handling back once we get a response from the server
//.then((result) => {
//    if (result.ok) {
//        return result.value;
//    }
//    const errorMessage =
//        result?.error?.detail && typeof result.error.detail === 'string'
//            ? result.error.detail
//            : 'Error updating items';
//    throw new Error(errorMessage);
//});

/**
 * GET users NFTs using the APIClient.
 */
export const callGetNFTs = (params: BaseParams & PageItemsSearchParams): Promise<YatPageItemsResponse> =>
    apiFactory()
        .call(searchYatPageItems, { value: params })
        .then((result) => {
            if (result.ok) {
                return result.value;
            }
            const errorMessage =
                result?.error?.detail && typeof result.error.detail === 'string'
                    ? result.error.detail
                    : 'Error getting groups';
            throw new Error(errorMessage);
        });

export async function getYatPageLinks(params: BaseParams & { eid: EmojiId }): Promise<ApiResult<YatPageItemsResponse>> {
    const target = links.yatPage().public(params.eid).search();

    return Request.postRequest<YatPageItemsResponse>({
        ...params,
        value: { query: [{ url: true }] },
        target: target,
        settings: {
            ...params.settings,
        },
    });
}

const pageItems = {
    addItem,
    getYatPageItems,
    updateYatPageItem,
    updateYatPageCollectionItems,
    fetchLiquidIdCollectionItems,
    callGetNFTCollectionAssets,
    callGetLiquidIdItems,
    callGetNFTCollectionDetails,
    callGetCollectionDetails,
    callGetNFTOwners,
    callGetUserAssets,
    callGetCollectionUserAssets,
    callGetUserStats,
    callGetCollectionTraits,
    callGetNFTDetails,
    updateAllQueryNFTs,
    callGetNFTs,
};

export { pageItems };
